'use client';

import * as React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, type LucideIcon } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils.js';

const alertVariants = cva('ed-alert', {
  variants: {
    variant: {
      danger:  'ed-alert-danger',
      warning: 'ed-alert-warning',
      info:    'ed-alert-info',
      success: 'ed-alert-success',
    },
  },
  defaultVariants: { variant: 'info' },
});

const DEFAULT_ICON: Record<NonNullable<VariantProps<typeof alertVariants>['variant']>, LucideIcon> = {
  danger:  AlertCircle,
  warning: AlertTriangle,
  info:    Info,
  success: CheckCircle2,
};

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  icon?: LucideIcon | false;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ variant = 'info', icon, className, children, ...props }, ref) => {
    const Icon = icon === false ? null : (icon ?? DEFAULT_ICON[variant ?? 'info']);
    return (
      <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
        {Icon && <Icon className="h-4 w-4" />}
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    );
  },
);
Alert.displayName = 'Alert';

export { Alert, alertVariants };
